import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { LESSONS, QUESTIONS } from '../data/academicCatalog';
import { buildReviewDates, emptyLearningProgress, loadLearningProgress, saveLearningProgress } from '../services/learningService';
import { INTRO_TRAIL } from '../data/introductoryTrail';

const LearningContext = createContext(null);
export const useLearning = () => useContext(LearningContext);

export function LearningProvider({ children }) {
  const { usuario } = useAuth();
  const [progress, setProgress] = useState(emptyLearningProgress);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    if (!usuario) { setProgress(emptyLearningProgress); setLoading(false); return undefined; }
    setLoading(true);
    loadLearningProgress(usuario.uid)
      .then(data => { if (active) setProgress(data); })
      .catch(error => console.error('Erro ao carregar progresso acadêmico:', error))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [usuario]);

  const persist = useCallback(async patch => {
    if (!usuario) return;
    setProgress(previous => ({ ...previous, ...patch }));
    try { await saveLearningProgress(usuario.uid, patch); }
    catch (error) { console.error('Erro ao salvar progresso acadêmico:', error); }
  }, [usuario]);

  const completeLesson = useCallback(async lesson => {
    if (progress.completedLessons?.[lesson.id]) return;
    const now = new Date().toISOString();
    const completedLessons = { ...progress.completedLessons, [lesson.id]: { completedAt: now, courseId: lesson.courseId, subjectId: lesson.subjectId } };
    const reviewSchedule = lesson.special ? progress.reviewSchedule : { ...progress.reviewSchedule, [lesson.id]: buildReviewDates() };
    await persist({ completedLessons, reviewSchedule, xp: (progress.xp || 0) + (lesson.special ? 10 : 30), lastActivity: { type: 'lesson', lessonId: lesson.id, at: now } });
  }, [persist, progress]);

  const answerQuestion = useCallback(async (question, answer) => {
    const correct = answer === question.correctAnswer;
    const previous = progress.questionAttempts?.[question.id] || { attempts: 0, errors: 0 };
    const attempt = { attempts: previous.attempts + 1, errors: previous.errors + (correct ? 0 : 1), lastAnswer: answer, correct, lastAttemptAt: new Date().toISOString() };
    const questionAttempts = { ...progress.questionAttempts, [question.id]: attempt };
    const errorNotebook = { ...progress.errorNotebook };
    if (!correct) errorNotebook[question.id] = { questionId: question.id, firstErrorAt: errorNotebook[question.id]?.firstErrorAt || attempt.lastAttemptAt, lastErrorAt: attempt.lastAttemptAt, errors: attempt.errors, corrected: false, lastAnswer: answer };
    else if (errorNotebook[question.id]) errorNotebook[question.id] = { ...errorNotebook[question.id], corrected: true, correctedAt: attempt.lastAttemptAt };
    await persist({ questionAttempts, errorNotebook, xp: (progress.xp || 0) + (correct && previous.attempts === 0 ? 5 : 0), lastActivity: { type: 'question', questionId: question.id, at: attempt.lastAttemptAt } });
    return correct;
  }, [persist, progress]);

  const completeIntroLesson = useCallback(async lessonId => {
    if (progress.introLessons?.[lessonId]) return;
    const at = new Date().toISOString();
    await persist({ introLessons: { ...progress.introLessons, [lessonId]: { completedAt: at } }, lastActivity: { type: 'intro', lessonId, at } });
  }, [persist, progress]);

  const completeReview = useCallback(async (lessonId, rating = 'remembered') => {
    const now = new Date(); const today = now.toISOString().slice(0,10);
    const entries = [...(progress.reviewSchedule?.[lessonId] || [])];
    const dueIndex = entries.findIndex(item => !item.completed && item.dueDate <= today);
    if (dueIndex >= 0) entries[dueIndex] = { ...entries[dueIndex], completed:true, completedAt:now.toISOString(), rating };
    const days = rating === 'forgot' ? 1 : rating === 'hard' ? 3 : Math.min(60, Math.max(7, (entries[dueIndex]?.intervalDays || 7) * 2));
    const next = new Date(now); next.setDate(next.getDate() + days);
    entries.push({ intervalDays:days, dueDate:next.toISOString().slice(0,10), completed:false, adaptive:true });
    const reviewHistory = [...(progress.reviewHistory || []), { lessonId, rating, completedAt:now.toISOString(), nextDueDate:next.toISOString().slice(0,10) }];
    await persist({ reviewSchedule:{...progress.reviewSchedule,[lessonId]:entries}, reviewHistory, lastActivity:{type:'review',lessonId,at:now.toISOString()} });
    return next.toISOString().slice(0,10);
  }, [persist, progress]);

  const metrics = useMemo(() => {
    const attempts = Object.values(progress.questionAttempts || {});
    const completed = Object.keys(progress.completedLessons || {}).length;
    const correct = attempts.filter(item => item.correct).length;
    const today = new Date().toISOString().slice(0, 10);
    const pendingReviews = Object.values(progress.reviewSchedule || {}).flat().filter(item => !item.completed && item.dueDate <= today).length;
    const totalLessons = LESSONS.filter(item => !item.special && item.published).length;
    return { completed, answered: attempts.length, accuracy: attempts.length ? Math.round(correct / attempts.length * 100) : 0, pendingReviews, content: totalLessons ? Math.round(completed / totalLessons * 100) : 0, domain: attempts.length ? Math.round(correct / attempts.length * 100) : 0 };
  }, [progress]);

  const getSubjectProgress = useCallback(subjectId => {
    const lessons = LESSONS.filter(item => item.subjectId === subjectId && item.published);
    const completed = lessons.filter(item => progress.completedLessons?.[item.id]).length;
    const questionIds = QUESTIONS.filter(item => item.subjectId === subjectId).map(item => item.id);
    const attempts = questionIds.map(id => progress.questionAttempts?.[id]).filter(Boolean);
    const correct = attempts.filter(item => item.correct).length;
    return { lessons: lessons.length, completed, percentage: lessons.length ? Math.round(completed / lessons.length * 100) : 0, answered: attempts.length, accuracy: attempts.length ? Math.round(correct / attempts.length * 100) : 0 };
  }, [progress]);

  const getCourseMetrics = useCallback(courseId => {
    if (!courseId) return { completed: 0, total: 0, content: 0, answered: 0, accuracy: 0, domain: 0, pendingReviews: 0, completedReviews: 0 };
    const lessons = LESSONS.filter(item => item.courseId === courseId && !item.special && item.published);
    const lessonIds = new Set(lessons.map(item => item.id));
    const questionIds = QUESTIONS.filter(item => lessonIds.has(item.lessonId)).map(item => item.id);
    const attempts = questionIds.map(id => progress.questionAttempts?.[id]).filter(Boolean);
    const correct = attempts.filter(item => item.correct).length;
    const reviews = lessons.flatMap(item => progress.reviewSchedule?.[item.id] || []);
    const completedReviews = reviews.filter(item => item.completed).length;
    const today = new Date().toISOString().slice(0, 10);
    const completed = lessons.filter(item => progress.completedLessons?.[item.id]).length;
    const accuracy = attempts.length ? Math.round(correct / attempts.length * 100) : 0;
    const reviewRate = reviews.length ? Math.round(completedReviews / reviews.length * 100) : 0;
    return { completed, total: lessons.length, content: lessons.length ? Math.round(completed / lessons.length * 100) : 0, answered: attempts.length, accuracy, domain: attempts.length ? Math.round(accuracy * .8 + reviewRate * .2) : 0, pendingReviews: reviews.filter(item => !item.completed && item.dueDate <= today).length, completedReviews };
  }, [progress]);

  const introMetrics = useMemo(() => ({ completed: Object.keys(progress.introLessons || {}).length, total: INTRO_TRAIL.lessons.length, percentage: Math.round(Object.keys(progress.introLessons || {}).length / INTRO_TRAIL.lessons.length * 100) }), [progress.introLessons]);

  return <LearningContext.Provider value={{ progress, loading, metrics, introMetrics, completeLesson, completeIntroLesson, completeReview, answerQuestion, getSubjectProgress, getCourseMetrics }}>{children}</LearningContext.Provider>;
}
